import { token } from '@/infrastructure/container';
import { type ILogger } from './Logger';

/**
 * Privacy-light product analytics: a couple of named events, no PII, no CSV content. The real
 * implementation POSTs to a same-origin Cloudflare Pages Function (`/api/event`) that records to
 * Workers Analytics Engine. Tests use the in-memory doubles below.
 */
export interface IAnalytics {
  /** A simulation run completed. Aggregate counts only — never SKU names or CSV content. */
  run(metrics: RunMetrics): void;
  /** A run failed. `category` is an enum-grade tag (DomainError.tag / Error.name), never a message. */
  error(category: string): void;
}

export interface RunMetrics {
  durationMs: number;
  recommendationCount: number;
  slotCount: number;
  quarantineCount: number;
}

export const TOKEN_IAnalytics = token<IAnalytics>('IAnalytics');

const ENDPOINT = '/api/event';
const MAX_CATEGORY = 64;

interface EventPayload {
  event: 'run' | 'error';
  durationMs?: number;
  recommendationCount?: number;
  slotCount?: number;
  quarantineCount?: number;
  category?: string;
}

export class Analytics implements IAnalytics {
  constructor(private logger: ILogger) {}

  run(metrics: RunMetrics): void {
    this.send({ event: 'run', ...metrics });
  }

  error(category: string): void {
    this.send({ event: 'error', category: category.slice(0, MAX_CATEGORY) });
  }

  private send(payload: EventPayload): void {
    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      } else {
        // .catch is essential: an un-caught rejection here would surface as an unhandledrejection.
        void fetch(ENDPOINT, {
          method: 'POST',
          body,
          keepalive: true,
          headers: { 'content-type': 'application/json' },
        }).catch(() => {});
      }
    } catch (error) {
      // Analytics must never break the app.
      this.logger.warn('Analytics send failed', error);
    }
  }
}

/** No-op double: the default in tests and a clean fallback when analytics is disabled. */
export class NoopAnalytics implements IAnalytics {
  run(_metrics: RunMetrics): void {
    void _metrics;
  }
  error(_category: string): void {
    void _category;
  }
}

/** Recording double for asserting which events a store emits. */
export class RecordingAnalytics implements IAnalytics {
  readonly runs: RunMetrics[] = [];
  readonly errors: string[] = [];
  run(metrics: RunMetrics): void {
    this.runs.push(metrics);
  }
  error(category: string): void {
    this.errors.push(category);
  }
}
